import React, { useContext, useEffect, useState, useRef } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { fetchFinancialData, fetchWalletData, fetchRecentActivity } from '../../api/apiService';
import { Button } from '../common/Button';
import LoadingSpinner from '../common/Loader';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Modal from '../common/Modal';

const DashboardScreen = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [financialData, setFinancialData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [walletData, setWalletData] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const location = useLocation();
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [totals, setTotals] = useState({
    totalEquity: 0,
    totalBalance: 0,
    totalCredit: 0,
    totalWithdrawals: 0,
    totalDeposits: 0,
  });
  const [dropdownVisible, setDropdownVisible] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);

  //console.log('Rendering DashboardScreen'); // Debugging log

  const toggleDropdown = (index, event) => {
    event.stopPropagation();
    const buttonRect = event.target.getBoundingClientRect();
    setDropdownPosition({
      top: buttonRect.bottom + window.scrollY,
      left: buttonRect.left + window.scrollX,
    });
    setDropdownVisible(dropdownVisible === index ? null : index);
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setDropdownVisible(null);
    }
  };

  const handleCreateDemoAccount = () => {
    navigate('create-account/demo');
  };

  const handleCreateRealAccount = () => {
    navigate('create-account/real');
  };

  const handleCreateWallet = () => {
    navigate('create-wallet');
  };

  useEffect(() => {
    if (location.state?.message) {
      //  console.log('Location state message:', location.state.message); // Debugging log
      setModalMessage(location.state.message);
      setIsSuccess(location.state.isSuccess || false);
      setShowModal(true);

      // Clear the state to avoid showing the modal again on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const closeModal = () => {
    //   console.log('Closing modal'); // Debugging log
    setShowModal(false);
    setModalMessage('');
  };

  useEffect(() => {
    //  console.log('Adding click outside listener'); // Debugging log
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      //    console.log('Removing click outside listener'); // Debugging log
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const calculateTotals = (accounts, wallets) => {
    // console.log('Calculating totals with accounts:', accounts, 'and wallets:', wallets); // Debugging log
    let totalEquity = 0;
    let totalBalance = 0;
    let totalCredit = 0;
    let totalWithdrawals = 0;
    let totalDeposits = 0;

    if (accounts && accounts.length > 0) {
      accounts.forEach((account) => {
        if (account.account_mode === 'Real') {
          totalEquity += parseFloat(account.equity);
          totalBalance += parseFloat(account.balance);
          totalCredit += parseFloat(account.credit);
          totalWithdrawals += parseFloat(account.withdrawal_amount);
          totalDeposits += parseFloat(account.deposit);
        }
      });
    }

    if (wallets && wallets.length > 0) {
      const totalWalletBalance = wallets.reduce((sum, wallet) => sum + parseFloat(wallet.balance), 0);
      totalBalance += totalWalletBalance;
    }

    //console.log('Calculated totals:', { totalEquity, totalBalance, totalCredit, totalWithdrawals, totalDeposits }); // Debugging log
    setTotals({
      totalEquity,
      totalBalance,
      totalCredit,
      totalWithdrawals,
      totalDeposits,
    });
  };

  useEffect(() => {
    // console.log('FinancialData or WalletData changed, recalculating totals'); // Debugging log
    calculateTotals(financialData, walletData);
  }, [financialData, walletData]);

  useEffect(() => {
    if (user) {
      //   console.log('User detected, fetching data and setting up WebSocket'); // Debugging log
      const { access_token, user_id } = user;
      setIsLoading(true);

      fetchFinancialData(access_token, user_id)
        .then((response) => {
          //     console.log('Financial Data API Response:', response); // Debugging log
          if (!response.error && response.data.trading_accounts) {
            setFinancialData(response.data.trading_accounts);
          }
        })
        .catch((error) => {
          console.error('Financial Data API Error:', error); // Debugging log
        });

      fetchWalletData(access_token, user_id)
        .then((response) => {
          //  console.log('Wallet Data API Response:', response); // Debugging log
          if (!response.error && response.data.wallets) {
            setWalletData(response.data.wallets);
          }
        })
        .catch((error) => {
          console.error('Wallet Data API Error:', error); // Debugging log
        });

      fetchRecentActivity(access_token, user_id)
        .then((response) => {
          //     console.log('Recent Activity API Response:', response); // Debugging log
          if (!response.error && response.data.recent_activity) {
            setRecentActivity(response.data.recent_activity);
          }
          setIsLoading(false);
        })
        .catch((error) => {
          //     console.error('Recent Activity API Error:', error); // Debugging log
          setIsLoading(false);
        });

      const ws = new WebSocket("wss://server.investain.com/crm/ws");
      ws.onopen = () => {
        //   console.log('WebSocket connection established'); // Debugging log
        const subscriptionMessage = {
          type: 'SUBSCRIBE',
          userId: user_id,
        };
        ws.send(JSON.stringify(subscriptionMessage));
      };

      ws.onmessage = (event) => {
        //    console.log('WebSocket message received:', event.data); // Debugging log
        const message = JSON.parse(event.data);
        if (message.type === 'ACCOUNT_FINANCIAL_UPDATE') {
          //    console.log('Updating financial data:', message.data); // Debugging log
          setFinancialData((prevData) => {
            // Merge new data with existing data
            const updatedData = prevData.map((account) => {
              const updatedAccount = message.data.find((acc) => acc.account_id === account.account_id);
              return updatedAccount ? { ...account, ...updatedAccount } : account;
            });
            return updatedData;
          });
        } else if (message.type === 'WALLET_UPDATE') {
          //   console.log('Updating wallet data:', message.data); // Debugging log
          setWalletData((prevData) => {
            // Merge new data with existing data
            const updatedData = prevData.map((wallet) => {
              const updatedWallet = message.data.find((w) => w.wallet_number === wallet.wallet_number);
              return updatedWallet ? { ...wallet, ...updatedWallet } : wallet;
            });
            return updatedData;
          });
        }
      };

      ws.onerror = (error) => {
        //    console.error('WebSocket Error:', error); // Debugging log
      };

      ws.onclose = () => {
        //   console.log('WebSocket connection closed'); // Debugging log
      };

      return () => {
        //  console.log('Cleaning up WebSocket connection'); // Debugging log
        ws.close();
      };
    }
  }, [user]);

  // console.log('Current financialData:', financialData); // Debugging log
  // console.log('Current walletData:', walletData); // Debugging log
  // console.log('Current recentActivity:', recentActivity); // Debugging log

  return (
    <div className="p-4 text-white" style={{ fontFamily: 'Rajdhani' }}>
      {isLoading && <LoadingSpinner />}

      <h1 className="text-2xl font-bold mb-4 text-text">Dashboard</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        {isLoading ? (
          Array.from({ length: 5 }).map((_, index) => (
            <div
              key={index}
              className="
          bg-white
          p-5
          rounded-2xl
          border border-gray-200
          shadow-2xl
          ring-1 ring-gray-100
        "
            >
              <div className="bar-loader h-6 mb-3"></div>
              <div className="bar-loader h-8"></div>
            </div>
          ))
        ) : (
          [
            { title: "Total Equity", value: totals.totalEquity.toFixed(2) },
            { title: "Total Balance", value: totals.totalBalance.toFixed(2) },
            { title: "Total Credit", value: totals.totalCredit.toFixed(2) },
            { title: "Total Deposits", value: totals.totalDeposits.toFixed(2) },
            { title: "Total Withdrawals", value: totals.totalWithdrawals.toFixed(2) },
          ].map((item, index) => (
            <div
              key={index}
              className="
          bg-white
          p-5
          rounded-2xl
          border border-gray-200
          shadow-2xl
          hover:shadow-[0_25px_60px_rgba(0,0,0,0.18)]
          hover:-translate-y-2
          transition-all
          duration-300
          cursor-pointer
        "
            >
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                {item.title}
              </h2>

              <p className="mt-2 text-3xl font-bold text-gray-900">
                {item.value}
              </p>
            </div>
          ))
        )}
      </div>
      {/* Display Live and Demo Accounts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        {/* Live Accounts Table */}
        <div className="bg-white p-4 rounded-lg shadow-lg">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-l font-bold mb-4 text-text">Live Accounts</h2>
            <Button onClick={handleCreateRealAccount} className="px-4 py-2">
              Create
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[#4d4d4d] rounded-lg">
              <thead>
                <tr className="bg-gray-200/50">
                  <th className="p-2 border-b border-gray-200">Account ID</th>
                  <th className="p-2 border-b border-gray-200">Leverage</th>
                  <th className="p-2 border-b border-gray-200">Equity</th>
                  <th className="p-2 border-b border-gray-200">Balance</th>
                  <th className="p-2 border-b border-gray-200">Margin</th>

                  <th className="p-2 border-b border-gray-200">Platforms</th>
                  <th className="p-2 border-b border-gray-200">Options</th>
                </tr>
              </thead>
              <tbody>
                {financialData
                  .filter((account) => account.account_mode === 'Real')
                  .map((account, index) => (
                    <tr key={index} className="hover:bg-gray-100">
                      <td className="p-2 border-b border-gray-200 text-center">{account.account_id}</td>
                      <td className="p-2 border-b border-gray-200 text-center">{account.leverage}</td>
                      <td className="p-2 border-b border-gray-200 text-center">{account.equity}</td>
                      <td className="p-2 border-b border-gray-200 text-center">{account.balance}</td>
                      <td className="p-2 border-b border-gray-200 text-center">{account.margin}</td>
                      <td className="p-2 border-b border-gray-200 text-center">{account.platforms}</td>
                      <td className="p-2 border-b border-gray-200 text-center">
                        <button
                          className="text-[#4d4d4d] focus:outline-none"
                          onClick={(e) => toggleDropdown(index, e)}
                        >
                          ...
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Demo Accounts Table */}
        <div className="bg-white p-4 rounded-lg shadow-lg">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-l font-bold mb-4 text-text">Demo Accounts</h2>
            <Button onClick={handleCreateDemoAccount} className="px-4 py-2">
              Create
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[#4d4d4d] rounded-lg">
              <thead>
                <tr className="bg-gray-200/50">
                  <th className="p-2 border-b border-gray-200">Account</th>
                  <th className="p-2 border-b border-gray-200">Leverage</th>
                  <th className="p-2 border-b border-gray-200">Equity</th>
                  <th className="p-2 border-b border-gray-200">Balance</th>
                  <th className="p-2 border-b border-gray-200">Margin</th>
                  <th className="p-2 border-b border-gray-200">Platforms</th>
                  <th className="p-2 border-b border-gray-200">Options</th>
                </tr>
              </thead>
              <tbody>
                {financialData
                  .filter((account) => account.account_mode === 'Demo' && account.account_id) // Skip empty or invalid entries
                  .map((account, index) => (
                    <tr key={index} className="hover:bg-gray-100">
                      <td className="p-2 border-b border-gray-200 text-center">{account.account_id}</td>
                      <td className="p-2 border-b border-gray-200 text-center">{account.leverage}</td>
                      <td className="p-2 border-b border-gray-200 text-center">{account.equity}</td>
                      <td className="p-2 border-b border-gray-200 text-center">{account.balance}</td>
                      <td className="p-2 border-b border-gray-200 text-center">{account.margin}</td>
                      <td className="p-2 border-b border-gray-200 text-center">{account.platforms}</td>
                      <td className="p-2 border-b border-gray-200 text-center">
                        <button
                          className="text-[#4d4d4d] focus:outline-none"
                          onClick={(e) => toggleDropdown(index, e)}
                        >
                          ...
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Dropdown Menu */}
      {dropdownVisible !== null && (
        <div
          ref={dropdownRef}
          className="fixed z-50 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5"
          style={{
            top: dropdownPosition.top,
            left: dropdownPosition.left,
          }}
        >
          <div className="py-1">
            <Link
              to={`/dashboard/deposit?account=${financialData[dropdownVisible].account_id}`}
              className="block px-4 py-2 text-sm font-bold text-[#4d4d4d] hover:bg-gray-100"
            >
              Deposit
            </Link>
            <Link
              to={`/dashboard/withdrawal?account=${financialData[dropdownVisible].account_id}`}
              className="block px-4 py-2 text-sm font-bold text-[#4d4d4d] hover:bg-gray-100"
            >
              Withdrawal
            </Link>
            <Link
              to={`/dashboard/transfer?account=${financialData[dropdownVisible].account_id}`}
              className="block px-4 py-2 text-sm font-bold text-[#4d4d4d] hover:bg-gray-100"
            >
              Transfer
            </Link>
          </div>
        </div>
      )}

      {/* Display Wallet Accounts */}
      <div className="bg-white p-4 rounded-lg shadow-lg mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-l font-bold text-text">Wallet Accounts</h2>
          <Button onClick={handleCreateWallet} className="px-4 py-2">
            Create Wallet
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[#4d4d4d] rounded-lg">
            <thead>
              <tr className="bg-gray-200/50">
                <th className="p-2 border-b border-gray-200">Account</th>
                <th className="p-2 border-b border-gray-200">Balance</th>
                <th className="p-2 border-b border-gray-200">Actions</th>
              </tr>
            </thead>
            <tbody>
              {walletData.map((wallet, index) => (
                <tr key={index} className="hover:bg-gray-100">
                  <td className="p-2 border-b border-gray-200 text-center">{wallet.wallet_number}</td>
                  <td className="p-2 border-b border-gray-200 text-center">{wallet.balance}</td>
                  <td className="p-2 border-b border-gray-200 text-center">
                    <div className="flex justify-center space-x-2 text-s">
                      <Link
                        to={`/dashboard/deposit?wallet=${wallet.wallet_number}`}
                        className="w-24 px-4 py-2 text-sm font-bold bg-transparent border border-primary text-primary rounded-lg transition-colors duration-200 hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                      >
                        Deposit
                      </Link>
                      <Link
                        to={`/dashboard/transfer?wallet=${wallet.wallet_number}`}
                        className="w-24 px-4 py-2 text-sm font-bold bg-transparent border border-primary text-primary rounded-lg transition-colors duration-200 hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                      >
                        Transfer
                      </Link>
                      <Link
                        to={`/dashboard/withdrawal?wallet=${wallet.wallet_number}`}
                        className="w-24 px-4 py-2 text-sm font-bold bg-transparent border border-primary text-primary rounded-lg transition-colors duration-200 hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                      >
                        Withdrawal
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Display Recent Activity */}
      <div className="bg-white p-4 rounded-lg shadow-lg mb-8">
        <h2 className="text-l font-bold mb-4 text-text">Recent Activity</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-[#4d4d4d] rounded-lg">
            <thead>
              <tr className="bg-gray-200/50">
                <th className="p-2 border-b border-gray-200 text-left">Date</th>
                <th className="p-2 border-b border-gray-200 text-left">Activity</th>
                <th className="p-2 border-b border-gray-200 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {recentActivity.map((activity, index) => (
                <tr key={index} className="hover:bg-gray-100">
                  <td className="p-2 border-b border-gray-200">{activity.date}</td>
                  <td className="p-2 border-b border-gray-200">{activity.description}</td>
                  <td className="p-2 border-b border-gray-200">{activity.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <Modal message={modalMessage} onClose={closeModal}>
          <div className={`p-4 ${isSuccess ? 'bg-green-100' : 'bg-red-100'} rounded-lg`}>
            <p className={`text-lg ${isSuccess ? 'text-green-700' : 'text-red-700'}`}>{modalMessage}</p>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default DashboardScreen;